import React, { useEffect, useMemo, useState } from 'react';
import { getGlobalResourceConfig } from '@opm/services';
import LoadingCommon from '@op/components/LoadingCommon';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { TRequestKeys } from '../FilterCommon/types';

type TResolveGameIds = (condData: Record<string, any>) => string[];

function useDefaultRequestWithCond({
  ids,
  extrRequestKeys,
  resolveGameIds,
  condGameIds,
}: {
  ids: string[];
  extrRequestKeys: TRequestKeys[];
  resolveGameIds: TResolveGameIds;
  condGameIds?: string[];
}) {
  const { condData, loading: condLoading } = useFilterCondData({
    keys: [],
    extrRequestKeys,
    condGameIds,
  });

  const gameIds = useMemo(
    () => resolveGameIds(condData),
    [condData, resolveGameIds],
  );

  const gameIdsKey = useMemo(
    () => gameIds.slice().sort().join(','),
    [gameIds],
  );

  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<Record<string, any>>({});

  useEffect(() => {
    if (condLoading) return;

    if (!ids.length) {
      setLoading(false);
      return;
    }

    if (!gameIdsKey) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getGlobalResourceConfig({
      data: {
        api_list: ids,
        game_ids: gameIds,
      },
    })
      .then((data: any) => {
        if (!cancelled) {
          setConfig(data || {});
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [condLoading, gameIdsKey, gameIds, ids]);

  return {
    loading: condLoading || loading,
    config,
    gameIds,
  };
}

export default function marketConnectWithCond<T>({
  ids,
  extrRequestKeys = [],
  useResolveGameIds,
  dealwith,
}: {
  ids: string[];
  extrRequestKeys?: TRequestKeys[];
  useResolveGameIds: (props: T) => TResolveGameIds;
  dealwith: (data: { config: Record<string, any>; gameIds: string[] }) => T;
}) {
  return (WrappedComponent: React.ComponentType<T>) => {
    return function Wrapper(props: T) {
      const resolveGameIds = useResolveGameIds(props);
      const condGameIds = (props as { condGameIds?: string[] }).condGameIds;
      const { loading, config, gameIds } = useDefaultRequestWithCond({
        ids,
        extrRequestKeys,
        resolveGameIds,
        condGameIds,
      });
      const realProps = dealwith({ config, gameIds });

      return loading ? (
        <LoadingCommon className="dbo-market-connect-with-cond-loading" isFullPage status="loading" />
      ) : (
        <WrappedComponent {...props} {...realProps} />
      );
    };
  };
}
